import React, { useEffect, useRef, useState } from 'react';

// THE SCHEDULE'S EDITORS — ported whole from legacy/app.js (2026-08-23,
// final phase): the + Add menu, the event form, the availability form, and
// the people picker they share. Pure React now — only Schedule.jsx ever
// opened them. The .sched-* / .type-chip / .people-* dress in
// legacy/style.css applies unchanged.

const LOCAL_TZ = (() => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || null; } catch { return null; }
})();
const inGroup = () => {
  try { return !!localStorage.getItem('missions_new:activeGroupId'); } catch { return false; }
};

// ── People — group members + free text (the guest list, not ownership) ───────
export function PeoplePicker({ preset, onChange }) {
  const [chosen, setChosen] = useState(() => (preset || [])
    .map((p) => (typeof p === 'string' ? { name: p } : p))
    .filter((p) => p && (p.name || p.user_id)));
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const me = window.currentUserId?.() || null;
  const key = (p) => (p.user_id || (p.name || '').toLowerCase());
  const has = (p) => chosen.some((c) => key(c) === key(p));
  const commit = (next) => { setChosen(next); onChange(next.slice()); };
  const add = (person) => {
    if (!person || (!person.name && !person.user_id)) return;
    commit(has(person) ? chosen : [...chosen, person]);
    setQ('');
    setOpen(true);
  };
  const members = (window.activeGroupMembers?.() || [])
    .map((m) => ({ user_id: m.user_id, name: m.username || m.user_id.slice(0, 6) }))
    .filter((m) => !has(m) && (!q.trim() || m.name.toLowerCase().includes(q.trim().toLowerCase())))
    .slice(0, 6);
  const offerFreeText = q.trim() && !members.some((m) => m.name.toLowerCase() === q.trim().toLowerCase());
  return (
    <div className="people-picker">
      <div className="people-field">
        <div className="people-chips">
          {chosen.map((p) => (
            <span key={key(p)} className={`people-chip${p.user_id ? '' : ' is-freetext'}`}
                  title={p.user_id ? undefined : 'not a group member — stored as a plain name'}>
              <span className="pc-name">{p.name || 'someone'}</span>
              <button type="button" className="pc-x" aria-label="Remove"
                      onClick={(e) => { e.stopPropagation(); commit(chosen.filter((c) => key(c) !== key(p))); }}>×</button>
            </span>
          ))}
        </div>
        <input type="text" className="people-input" autoComplete="off"
               placeholder={(window.activeGroupMembers?.() || []).length ? 'add someone…' : 'add a name…'}
               value={q}
               onChange={(e) => { setQ(e.target.value); setOpen(true); }}
               onFocus={() => setOpen(true)}
               onBlur={() => setTimeout(() => setOpen(false), 140)}
               onKeyDown={(e) => {
                 if (e.key === 'Enter') {
                   e.preventDefault();
                   if (open && members[0]) add(members[0]);
                   else if (q.trim()) add({ name: q.trim() });
                 } else if (e.key === 'Escape') setOpen(false);
                 else if (e.key === 'Backspace' && !q && chosen.length) commit(chosen.slice(0, -1));
               }} />
      </div>
      {open && (members.length || offerFreeText) ? (
        <div className="people-menu">
          {members.map((m) => (
            <button key={m.user_id} type="button" className="people-menu-row"
                    onMouseDown={(e) => { e.preventDefault(); add(m); }}>
              {m.name}{m.user_id === me ? ' (you)' : ''}
            </button>
          ))}
          {offerFreeText && (
            <button type="button" className="people-menu-row is-new"
                    onMouseDown={(e) => { e.preventDefault(); add({ name: q.trim() }); }}>
              add “{q.trim()}”
            </button>
          )}
        </div>
      ) : null}
    </div>
  );
}

function Chips({ options, value, onPick, dataKey }) {
  return (
    <div className="type-chips">
      {options.map((o) => (
        <button key={o.key} type="button" className={`type-chip${o.key === value ? ' on' : ''}`}
                title={o.hint || undefined}
                onClick={() => onPick(o.key)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

const CADENCES = [
  { key: 'none', label: 'once' }, { key: 'daily', label: 'daily' },
  { key: 'weekly', label: 'weekly' }, { key: 'monthly', label: 'monthly' },
];
const DOWS = [
  { i: 1, label: 'M' }, { i: 2, label: 'T' }, { i: 3, label: 'W' },
  { i: 4, label: 'T' }, { i: 5, label: 'F' }, { i: 6, label: 'S' }, { i: 0, label: 'S' },
];

function DowChips({ selected, onToggle }) {
  return (
    <div className="type-chips">
      {DOWS.map((d, idx) => (
        <button key={idx} type="button" className={`type-chip${selected.has(d.i) ? ' on' : ''}`}
                onClick={() => onToggle(d.i)}>{d.label}</button>
      ))}
    </div>
  );
}

// ── The + Add menu — what happens / your time ────────────────────────────────
export function AddMenu({ at, dateIso, onPick, onClose }) {
  const ref = useRef(null);
  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const t = setTimeout(() => document.addEventListener('pointerdown', close), 0);
    return () => { clearTimeout(t); document.removeEventListener('pointerdown', close); };
  }, [onClose]);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let top, left;
    if (at.rect) { top = at.rect.bottom + 6; left = Math.max(8, at.rect.right - el.offsetWidth); }
    else { top = at.y + 6; left = Math.min(at.x, window.innerWidth - el.offsetWidth - 8); }
    el.style.top = `${Math.max(8, Math.min(top, window.innerHeight - el.offsetHeight - 8))}px`;
    el.style.left = `${Math.max(8, left)}px`;
  }, [at]);
  const items = [
    { group: 'On the calendar' },
    { label: 'Event', hint: 'something that happens at a time', pick: { kind: 'event', dateIso } },
    { group: 'Your time' },
    { label: 'Busy', hint: "time you're not schedulable", pick: { kind: 'avail', availKind: 'busy', dateIso } },
    { label: 'Free', hint: 'time open for scheduling', pick: { kind: 'avail', availKind: 'free', dateIso } },
    { label: 'Working hours',
      hint: inGroup() ? 'the bounds of your week in the group' : "the bounds of your week — when you're reachable at all",
      pick: { kind: 'avail', availKind: 'working', dateIso } },
  ];
  return (
    <div ref={ref} className="sched-add-menu" id="sched-add-menu">
      {items.map((it, i) => it.group
        ? <div key={i} className="sam-group">{it.group}</div>
        : (
          <button key={i} type="button" className="sam-item"
                  onClick={() => { onClose(); onPick(it.pick); }}>
            <span className="sam-label">{it.label}</span>
            <span className="sam-hint">{it.hint}</span>
          </button>
        ))}
    </div>
  );
}

// ── The availability form — busy / free / working hours ──────────────────────
export function AvailabilityForm({ dateIso, availKind, existing, onDone, onClose }) {
  const isEdit = !!existing;
  const kinds = [
    { k: 'busy', label: 'busy', hint: 'not schedulable' },
    { k: 'free', label: 'free', hint: 'open for scheduling' },
    { k: 'working', label: 'working hours', hint: inGroup() ? 'the bounds of your week in the group' : "the bounds of your week — when you're reachable at all" },
  ];
  const exRec = isEdit && existing.recurrence ? existing.recurrence : null;
  const [kind, setKind] = useState((isEdit ? existing.kind : availKind) || 'busy');
  const [label, setLabel] = useState(isEdit ? (existing.title || '') : '');
  const [date, setDate] = useState(isEdit && existing.start_at ? existing.start_at.slice(0, 10) : dateIso);
  const [start, setStart] = useState(isEdit && existing.start_at ? existing.start_at.slice(11, 16) : '09:00');
  const [end, setEnd] = useState(isEdit && existing.end_at ? existing.end_at.slice(11, 16) : '17:00');
  const [cadence, setCadence] = useState(exRec?.cadence || 'none');
  const [dows, setDows] = useState(() => new Set(Array.isArray(exRec?.days_of_week) ? exRec.days_of_week : []));
  const peopleRef = useRef(isEdit ? (existing.people || []) : []);
  const titleRef = useRef(null);
  useEffect(() => { titleRef.current?.focus(); }, []);
  const cur = kinds.find((x) => x.k === kind) || kinds[0];

  const save = async () => {
    const lab = label.trim() || (kind === 'free' ? 'Free' : kind === 'working' ? 'Working hours' : 'Busy');
    const start_at = `${date}T${start || '00:00'}:00`;
    const end_at = `${date}T${end || '23:59'}:00`;
    let recurrence = null;
    if (cadence !== 'none') {
      recurrence = { cadence, ...(LOCAL_TZ ? { timezone: LOCAL_TZ } : {}) };
      if (cadence === 'weekly' && dows.size) recurrence.days_of_week = [...dows];
    }
    try {
      if (isEdit) {
        await window.putJson(`/api/items/${existing.id}`, {
          title: lab,
          value_json: {
            kind, start_at: window.withLocalOffset(start_at), end_at: window.withLocalOffset(end_at),
            label: lab, recurrence, people: peopleRef.current,
          },
        });
      } else {
        const r = await fetch(window.maybeScopeUrl('/api/availability'), {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            kind, start_at: window.withLocalOffset(start_at), end_at: window.withLocalOffset(end_at),
            label: label.trim(), ...(recurrence ? { recurrence } : {}), people: peopleRef.current,
          }),
        });
        if (!r.ok) { const b = await r.json().catch(() => ({})); throw new Error(b.error || r.statusText); }
      }
      onDone();
    } catch (e) { alert(`Couldn't save — ${e.message}`); }
  };

  return (
    <div className="sched-event-form" id="sched-avail-form">
      <div className="row">
        <span className="row-meta">mark as</span>
        <Chips options={kinds.map((x) => ({ key: x.k, label: x.label }))} value={kind} onPick={setKind} />
      </div>
      <input ref={titleRef} type="text" className="se-title" value={label}
             placeholder={`label (optional) — e.g. ${cur.hint}`}
             onChange={(e) => setLabel(e.target.value)} />
      <div className="row">
        <span className="row-meta">when</span>
        <input type="date" className="se-date" value={date} onChange={(e) => setDate(e.target.value)} />
        <input type="time" className="se-start" value={start} onChange={(e) => setStart(e.target.value)} />
        <span className="row-meta">to</span>
        <input type="time" className="se-end" value={end} onChange={(e) => setEnd(e.target.value)} />
      </div>
      <div className="row">
        <span className="row-meta">repeats</span>
        <Chips options={CADENCES} value={cadence} onPick={setCadence} />
      </div>
      {cadence === 'weekly' && (
        <div className="row">
          <span className="row-meta">on</span>
          <DowChips selected={dows} onToggle={(i) => setDows((cur2) => {
            const next = new Set(cur2);
            if (next.has(i)) next.delete(i); else next.add(i);
            return next;
          })} />
        </div>
      )}
      <div className="row">
        <span className="row-meta">with</span>
        <span><PeoplePicker preset={peopleRef.current} onChange={(p) => { peopleRef.current = p; }} /></span>
      </div>
      <div className="form-actions">
        <button type="button" className="save-btn" onClick={save}>{isEdit ? 'update' : 'save'}</button>
        <button type="button" className="cancel-btn" onClick={onClose}>cancel</button>
        {isEdit && (
          <button type="button" className="del-btn" style={{ marginLeft: 'auto' }}
                  onClick={async () => {
                    try {
                      const r = await fetch(window.maybeScopeUrl(`/api/availability/${existing.id}`), { method: 'DELETE', credentials: 'same-origin' });
                      if (!r.ok) { const b = await r.json().catch(() => ({})); throw new Error(b.error || r.statusText); }
                      onDone();
                    } catch (e) { alert(`Couldn't delete — ${e.message}`); }
                  }}>delete</button>
        )}
      </div>
    </div>
  );
}

// ── The event form ───────────────────────────────────────────────────────────
export function EventForm({ dateIso, existing, onDone, onClose }) {
  const isEdit = !!existing;
  const types = [
    { key: 'meeting', label: 'meeting' },
    { key: 'personal', label: 'personal' },
    { key: 'focus_work', label: 'focus work' },
    { key: 'other', label: 'other' },
  ];
  const presetRec = isEdit && existing.recurrence ? existing.recurrence : null;
  const [title, setTitle] = useState(isEdit ? (existing.title || '') : '');
  const [date, setDate] = useState(isEdit && existing.start_at ? existing.start_at.slice(0, 10) : dateIso);
  const [start, setStart] = useState(isEdit && existing.start_at ? existing.start_at.slice(11, 16) : '');
  const [end, setEnd] = useState(isEdit && existing.end_at && existing.end_at !== existing.start_at ? existing.end_at.slice(11, 16) : '');
  const [type, setType] = useState(isEdit ? (existing.event_type || (existing.is_block ? 'focus_work' : 'other')) : 'other');
  const [attention, setAttention] = useState(isEdit && existing.attention === 'passive' ? 'passive' : 'exclusive');
  const [notes, setNotes] = useState(isEdit ? (existing.notes || '') : '');
  const [notesOpen, setNotesOpen] = useState(!!(isEdit && existing.notes));
  const [cadence, setCadence] = useState(presetRec?.cadence || 'none');
  const [dows, setDows] = useState(() => new Set(Array.isArray(presetRec?.days_of_week) ? presetRec.days_of_week : []));
  const [extraTimes, setExtraTimes] = useState(() => (Array.isArray(presetRec?.times) && presetRec.times.length > 1)
    ? presetRec.times.slice(1).map((t) => ({ start: t.start || '', end: t.end || '' })) : []);
  const peopleRef = useRef(isEdit ? (existing.people || []) : []);
  const titleRef = useRef(null);
  useEffect(() => { titleRef.current?.focus(); }, []);

  const save = async () => {
    const t = title.trim();
    if (!t) return;
    const start_at = start ? `${date}T${start}:00` : `${date}T00:00:00`;
    const end_at = end ? `${date}T${end}:00` : undefined;
    let recurrence = null;
    if (cadence !== 'none') {
      const extras = extraTimes
        .map((r) => (r.start ? { start: r.start, ...(r.end ? { end: r.end } : {}) } : null))
        .filter(Boolean);
      const mainSlot = start ? { start, ...(end ? { end } : {}) } : null;
      const times = extras.length && mainSlot ? [mainSlot, ...extras] : null;
      if (cadence === 'weekly') {
        recurrence = { cadence: 'weekly', ...(dows.size ? { days_of_week: [...dows] } : {}), ...(times ? { times } : {}), ...(LOCAL_TZ ? { timezone: LOCAL_TZ } : {}) };
      } else {
        recurrence = { cadence, ...(times ? { times } : {}), ...(LOCAL_TZ ? { timezone: LOCAL_TZ } : {}) };
      }
    }
    try {
      if (isEdit) {
        const value_json = {
          title: t,
          start_at: window.withLocalOffset(start_at),
          ...(end_at ? { end_at: window.withLocalOffset(end_at) } : { end_at: window.withLocalOffset(start_at) }),
          event_type: type,
          is_block: null,
          attention: attention === 'passive' ? 'passive' : null,
          ...(notes.trim() ? { notes: notes.trim() } : {}),
          recurrence,
          people: peopleRef.current,
        };
        await window.putJson(`/api/items/${existing.id}`, { title: t, value_json });
      } else {
        const r = await fetch(window.maybeScopeUrl('/api/events'), {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            title: t, event_date: date, start_at,
            ...(end_at ? { end_at } : {}),
            event_type: type,
            ...(attention === 'passive' ? { attention: 'passive' } : {}),
            ...(notes.trim() ? { notes: notes.trim() } : {}),
            ...(recurrence ? { recurrence } : {}),
            people: peopleRef.current,
          }),
        });
        if (!r.ok) { const b = await r.json().catch(() => ({})); throw new Error(b.error || r.statusText); }
      }
      onDone();
    } catch (e) { alert(`Couldn't save — ${e.message}`); }
  };

  return (
    <div className="sched-event-form" id="sched-event-form">
      <input ref={titleRef} type="text" className="se-title" placeholder="what’s happening?"
             value={title} onChange={(e) => setTitle(e.target.value)} />
      <div className="row">
        <span className="row-meta">when</span>
        <input type="date" className="se-date" value={date} onChange={(e) => setDate(e.target.value)} />
        <input type="time" className="se-start" value={start} onChange={(e) => setStart(e.target.value)} />
        <span className="row-meta">to</span>
        <input type="time" className="se-end" value={end} onChange={(e) => setEnd(e.target.value)} />
      </div>
      <div className="row">
        <span className="row-meta">repeats</span>
        <Chips options={CADENCES} value={cadence} onPick={setCadence} />
      </div>
      {cadence === 'weekly' && (
        <div className="row">
          <span className="row-meta">on</span>
          <DowChips selected={dows} onToggle={(i) => setDows((cur) => {
            const next = new Set(cur);
            if (next.has(i)) next.delete(i); else next.add(i);
            return next;
          })} />
        </div>
      )}
      {cadence !== 'none' && (
        <div className="row">
          <span className="row-meta">also at</span>
          <div className="extra-times">
            {extraTimes.map((r, i) => (
              <div className="time-slot" key={i}>
                <input type="time" className="ts-start" value={r.start}
                       onChange={(e) => setExtraTimes((cur) => cur.map((x, j) => (j === i ? { ...x, start: e.target.value } : x)))} />
                <span className="row-meta">to</span>
                <input type="time" className="ts-end" value={r.end}
                       onChange={(e) => setExtraTimes((cur) => cur.map((x, j) => (j === i ? { ...x, end: e.target.value } : x)))} />
                <button type="button" className="ts-del" title="Remove time"
                        onClick={() => setExtraTimes((cur) => cur.filter((_, j) => j !== i))}>×</button>
              </div>
            ))}
          </div>
          <button type="button" className="more-link"
                  onClick={() => setExtraTimes((cur) => [...cur, { start: '', end: '' }])}>+ another time</button>
        </div>
      )}
      <div className="row">
        <span className="row-meta">type</span>
        <Chips options={types} value={type} onPick={setType} />
      </div>
      <div className="row">
        <span className="row-meta">attention</span>
        <Chips value={attention} onPick={setAttention}
               options={[
                 { key: 'exclusive', label: 'focused', hint: 'Occupies the time — nothing else can be scheduled here' },
                 { key: 'passive', label: 'background', hint: 'Light — you can work over it (a podcast, a commute); a small action can run alongside' },
               ]} />
      </div>
      <div className="row">
        <span className="row-meta">with</span>
        <span><PeoplePicker preset={peopleRef.current} onChange={(p) => { peopleRef.current = p; }} /></span>
      </div>
      {notesOpen && (
        <div className="row">
          <span className="row-meta">notes</span>
          <input type="text" className="se-notes" placeholder="anything else…" autoFocus={!notes}
                 value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>
      )}
      <div className="form-actions">
        <button type="button" className="save-btn" onClick={save}>{isEdit ? 'update' : 'save'}</button>
        <button type="button" className="cancel-btn" onClick={onClose}>cancel</button>
        {!notesOpen && <button type="button" className="more-link" onClick={() => setNotesOpen(true)}>+ note</button>}
        {isEdit && (
          <button type="button" className="del-btn" style={{ marginLeft: 'auto' }}
                  onClick={async () => {
                    try {
                      const r = await fetch(window.maybeScopeUrl(`/api/events/${existing.id}`), { method: 'DELETE', credentials: 'same-origin' });
                      if (!r.ok) { const b = await r.json().catch(() => ({})); throw new Error(b.error || r.statusText); }
                      onDone();
                    } catch (e) { alert(`Couldn't delete — ${e.message}`); }
                  }}>delete</button>
        )}
      </div>
    </div>
  );
}
